// Invite wizard step 1 — who is visiting. RON-5466 Phase 2. Prototype: `InviteStep1`.
//
// One visit row is created per visitor on submit; they share a `group` id so Phase 3 can offer
// group-cancel. Visitor identity stays inline on the visit (db-schema §5) — visitors are ad-hoc
// and do not get a record of their own, unlike contractors.
//
// Fields use the app's house field markup via VisitorFormRow, so they look and behave like the
// guest-invite form rather than like bare Bootstrap inputs.

import { useTranslation } from "react-i18next";

import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";

export const EMPTY_VISITOR = { name: "", email: "", company: "" };

const FIELDS = [
  { key: "name", labelKey: "react_visitors_field_name", type: "text", required: true },
  { key: "email", labelKey: "react_visitors_field_email", type: "email", required: true },
  { key: "company", labelKey: "react_visitors_field_company", type: "text" },
];

const VisitorDetailsStep = ({
  visitors,
  errors,
  onChange,
  onAdd,
  onRemove,
  // False when editing: a visit row is one guest, so there is nobody to add or remove.
  allowMultiple = true,
}) => {
  const { t } = useTranslation("common");

  return (
    <>
      {visitors.map((visitor, index) => (
        <div className="visitor-card" key={index}>
          <div className="visitor-card__head">
            <p className="visitor-card__title">
              {visitors.length > 1
                ? t("react_visitors_visitor_n").replace("%s", String(index + 1))
                : t("react_visitors_details_title")}
            </p>
            {/* The first visitor cannot be removed — a visit needs at least one. */}
            {allowMultiple && index > 0 && (
              <button
                type="button"
                className="visitor-card__remove"
                onClick={() => onRemove(index)}
              >
                {t("react_visitors_remove")}
              </button>
            )}
          </div>

          {FIELDS.map((field) => {
            const fieldError = errors?.[index]?.[field.key];
            const inputId = `visitor-${index}-${field.key}`;
            // House convention (see CalendarEvent's guest form): a required field carries a `*`
            // on both its label and its placeholder.
            const label = `${t(field.labelKey)}${field.required ? "*" : ""}`;

            return (
              <VisitorFormRow
                key={field.key}
                id={inputId}
                label={label}
                error={fieldError ? t(fieldError) : null}
              >
                <input
                  id={inputId}
                  type={field.type}
                  placeholder={label}
                  className={fieldClassName(visitor[field.key], fieldError)}
                  value={visitor[field.key]}
                  onChange={(event) => onChange(index, field.key, event.target.value)}
                />
              </VisitorFormRow>
            );
          })}
        </div>
      ))}

      {allowMultiple && (
        <button type="button" className="visitor-add-more" onClick={onAdd}>
          {t("react_visitors_add_another")}
        </button>
      )}
    </>
  );
};

export default VisitorDetailsStep;
